// Tournament Service - API client for www.galea.com/padel-tournaments
import { z } from 'zod';

// --- API Config ---

const API_BASE = 'https://www.galea.org/padel-tournaments/api';

// --- Types ---

export interface TournamentConfig {
  id: string;
  date: string; // ISO date
  time: string; // e.g. "18:30"
  duration: number; // minutes
  location: string;
  locationUrl: string;
  courts: number[]; // e.g. [1, 3, 5]
  maxPlayers: number; // courts.length * 4
  level: string;
  announcementText: string;
  announcementTextEn: string;
  tournamentFormat: 'mexicano' | 'americano' | 'team_americano';
  doorCode: string;
}

export interface SignupPartner {
  name: string;
  email: string;
  phone: string;
}

export interface Signup {
  id: string;
  tournamentId: string;
  name: string;
  email: string;
  phone: string;
  partner?: SignupPartner;
  signedUpAt: string; // ISO date
}

// --- Validation ---

const playerFields = {
  name: z.string().trim().min(2, 'Navn må ha minst 2 tegn / Name must be at least 2 characters').max(100),
  email: z.string().trim().email('Ugyldig e-post / Invalid email').max(255),
  phone: z.string().trim().min(8, 'Ugyldig telefonnummer / Invalid phone number').max(20)
    .regex(/^[+\d\s()-]+$/, 'Ugyldig telefonnummer / Invalid phone number'),
};

export const signupSchema = z.object(playerFields);

export const teamSignupSchema = z.object({
  ...playerFields,
  partnerName: z.string().trim().max(100).optional().or(z.literal('')),
  partnerEmail: z.string().trim().max(255).optional().or(z.literal('')),
  partnerPhone: z.string().trim().max(20).optional().or(z.literal('')),
}).refine((data) => {
  const hasAny = data.partnerName || data.partnerEmail || data.partnerPhone;
  if (!hasAny) return true;
  return data.partnerName && data.partnerName.length >= 2
    && data.partnerEmail && /^[^@]+@[^@]+\.[^@]+$/.test(data.partnerEmail)
    && data.partnerPhone && data.partnerPhone.length >= 8 && /^[+\d\s()-]+$/.test(data.partnerPhone);
}, {
  message: 'Fyll ut alle feltene for partner / Fill in all partner fields',
  path: ['partnerName'],
});

// --- JWT Token Management ---

const TOKEN_KEY = 'padel_admin_token';

export function getAdminToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAdminToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAdminToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

function authHeaders(): Record<string, string> {
  const token = getAdminToken();
  if (token) return { Authorization: `Bearer ${token}` };
  return {};
}

// --- Default tournament (fallback if API is unreachable) ---

const DEFAULT_TOURNAMENT: TournamentConfig = {
  id: 'default',
  date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  time: '18:30',
  duration: 90,
  location: 'Padelon Trondheim, Fossegrenda',
  locationUrl: 'https://maps.app.goo.gl/7ZxUZPjZHUzavzi2A',
  courts: [1, 2, 3],
  maxPlayers: 12,
  level: 'Alle nivåer / All levels',
  announcementText: 'Sosial turnering med NTNUi Padel! Vi gleder oss!',
  announcementTextEn: 'Social tournament with NTNUi Padel! We are excited!',
  tournamentFormat: 'americano',
  doorCode: '2022#',
};

// --- API helper ---

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...options?.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `API error ${res.status}`);
  }
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return {} as T;
  }
  return res.json();
}

// --- Raw API response shape ---

interface RawTournamentResponse {
  id: string;
  config: Omit<TournamentConfig, 'id'>;
  signups?: Signup[];
}

// --- Tournament CRUD ---

function normalizeTournament(raw: RawTournamentResponse): TournamentConfig {
  const config = { ...raw.config, id: raw.id };
  // Normalize courts: if API returns a number, convert to array
  if (typeof config.courts === 'number') {
    config.courts = Array.from({ length: config.courts }, (_, i) => i + 1);
  }
  config.maxPlayers = config.courts.length * 4;
  return config;
}

export async function getAllTournaments(): Promise<TournamentConfig[]> {
  try {
    const tournaments = await apiFetch<RawTournamentResponse[]>('/tournaments');
    return tournaments.map(normalizeTournament);
  } catch (e) {
    console.error('Error fetching tournaments:', e);
    return [];
  }
}

export async function getTournament(): Promise<TournamentConfig> {
  try {
    const tournaments = await getAllTournaments();
    if (tournaments.length > 0) {
      // Return the next upcoming tournament (sorted by date)
      const sorted = tournaments.sort((a, b) => a.date.localeCompare(b.date));
      const upcoming = sorted.find(t => new Date(t.date) >= new Date(new Date().toDateString()));
      return upcoming || sorted[sorted.length - 1];
    }
  } catch (e) {
    console.error('Error fetching tournament:', e);
  }
  return { ...DEFAULT_TOURNAMENT };
}

export async function createTournament(config: Omit<TournamentConfig, 'id'>): Promise<TournamentConfig> {
  const body = { ...config, maxPlayers: config.courts.length * 4 };
  const created = await apiFetch<TournamentConfig>('/tournaments', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return normalizeTournament(created as unknown as RawTournamentResponse);
}

export async function saveTournament(config: TournamentConfig): Promise<TournamentConfig> {
  config.maxPlayers = config.courts.length * 4;
  const isNew = !config.id || config.id === 'default';
  if (isNew) {
    const { id: _removed, ...body } = config;
    const created = await apiFetch<TournamentConfig>('/tournaments', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return created;
  }
  await apiFetch(`/tournaments/${config.id}`, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
  return config;
}

// --- Signup CRUD ---

export async function getSignups(tournamentId?: string): Promise<Signup[]> {
  if (!tournamentId) return [];
  try {
    const data = await apiFetch<{ signups: Signup[] }>(`/tournaments/${tournamentId}`);
    return data.signups || [];
  } catch (e) {
    console.error('Error fetching signups:', e);
    return [];
  }
}

export async function addSignup(signup: Omit<Signup, 'id' | 'signedUpAt'>): Promise<{ success: boolean; message: string; signup?: Signup }> {
  try {
    const body: Record<string, any> = {
      name: signup.name,
      email: signup.email,
      phone: signup.phone,
    };
    if (signup.partner) {
      body.partner = signup.partner;
    }
    const result = await apiFetch<{ signup: Signup }>(`/tournaments/${signup.tournamentId}/signups`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { success: true, message: 'Påmelding vellykket / Signup successful', signup: result.signup };
  } catch (e: any) {
    return { success: false, message: e.message || 'Signup failed' };
  }
}

export async function removeSignup(tournamentId: string, signupId: string): Promise<boolean> {
  try {
    await apiFetch(`/tournaments/${tournamentId}/signups/${signupId}`, {
      method: 'DELETE',
    });
    return true;
  } catch (e) {
    console.error('Error removing signup:', e);
    return false;
  }
}

// --- Admin ---

export async function verifyAdminPassword(password: string): Promise<{ success: boolean; error?: string }> {
  try {
    const result = await apiFetch<{ token: string }>('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
    setAdminToken(result.token);
    return { success: true };
  } catch (e: any) {
    const msg = e.message || 'Login failed';
    console.error('Admin login error:', msg);
    return { success: false, error: msg };
  }
}

// --- Helpers (pure, synchronous — operate on already-fetched data) ---

export function getTournamentDateTime(config: TournamentConfig): Date {
  const time = config.time || '18:00';
  const [hours, minutes] = time.split(':').map(Number);
  const dateParts = (config.date || '').split('-').map(Number);
  const date = dateParts.length === 3
    ? new Date(dateParts[0], dateParts[1] - 1, dateParts[2])
    : new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}

export function isSignupOpen(config: TournamentConfig): boolean {
  const tournamentTime = getTournamentDateTime(config);
  const opensAt = new Date(tournamentTime.getTime() - 72 * 60 * 60 * 1000);
  return new Date() >= opensAt;
}

export function getSignupOpensAt(config: TournamentConfig): Date {
  const tournamentTime = getTournamentDateTime(config);
  return new Date(tournamentTime.getTime() - 72 * 60 * 60 * 1000);
}

export async function getParticipantsAndWaitlist(config: TournamentConfig): Promise<{ participants: Signup[]; waitlist: Signup[] }> {
  const signups = (await getSignups(config.id)).sort(
    (a, b) => new Date(a.signedUpAt).getTime() - new Date(b.signedUpAt).getTime()
  );
  return {
    participants: signups.slice(0, config.maxPlayers),
    waitlist: signups.slice(config.maxPlayers),
  };
}
